// src/App.js
import React, { useState, useEffect, useContext } from 'react';
import { BrowserRouter, Route, Routes, useLocation, useParams, Navigate } from "react-router-dom";
import { DataContext, DataProvider } from './context/DataContext';
import { AnalyticsProvider } from './context/AnalyticsContext';
import { App as AntApp, ConfigProvider } from 'antd';
import { antDesignTheme } from './theme/antDesignTheme';
import {
  getBaseData,
  getHomePageEvents,
  getAllEvents,
  getSingleEvent,
  calculateDistance,
  getOrganizationInfo,
  getCurrentUser,
  getAllUsers,
  getAllOrganizations
} from './information.service';

import Home from "./Home";
import Events from "./Events";
import AdminPanel from "./AdminPanel";
import EventShell from "./EventShell";
import Profile from "./Profile";
import OrganizerHome from "./OrganizerHome";
import OrganizerSettings from "./OrganizerSettings";
import Loading from "./Loading";
import ScrollToTop from "./ScrollToTop";
import ErrorPage from "./ErrorPage";
import NewCreateEvent from "./NewCreateEvent";
import ResetPassword from "./ResetPassword";
import Redirect from './Redirect';
import Lists from "./Lists";
import MyOrganizations from "./MyOrganizations";
import CreateOrganization from "./CreateOrganization";
import OrganizationProfile from "./OrganizationProfile";
import OnboardingQuiz from "./OnboardingQuiz";
import DynamicPromptModal from './components/DynamicPromptModal';
import useUserPrompts from './hooks/useUserPrompts';
import { RouteErrorBoundary } from "./components/RouteErrorBoundary";

// const redirectToExternalBrowser = () => {
//   const userAgent = navigator.userAgent || navigator.vendor || window.opera;
//   if (userAgent.match(/Instagram/i)) {
//     // TODO: Once the redirect actually works this can be changed to this code instead since we don't need different behavior for Android vs. iPhone
//     // We only care if the user is trying to acces the URL directly from Instagram
//     // window.location.href = "http://localhost:3000/redirect/";

//     if (/android/i.test(userAgent)) {
//       window.location.href = "intent://www.ummahfy.com#Intent;scheme=https;package=com.android.chrome;end";
//     } else if (/iPad|iPhone/i.test(userAgent)) {
//       window.location.href = "http://localhost:3000/redirect/";
//     }
//   }
// };

/** Remount when :id changes so event fetch / local state never show the wrong event. */
function EventRoute() {
  const { id } = useParams();
  return <EventShell key={id} />;
}

/**
 * Gate the admin route at the router level. Signed-out users (no userInfo) and
 * signed-in non-admins both get bounced to `/` instead of seeing the page or
 * even the "access denied" placeholder. Initial app data has already loaded
 * by the time any Route renders (App.js shows <Loading/> until then), so we
 * don't need a separate "still loading" branch here.
 */
function RequireAdmin({ children }) {
  const { userInfo } = useContext(DataContext);
  if (!userInfo || !userInfo.isAdmin) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function AppContent() {
  const location = useLocation();

  // REDIRECT FOR SUHOORFEST
  useEffect(() => {
    if (location.pathname === "/chess") {
      window.location.replace("/event/8432");
    }
  }, [location]);

  const { setAppData } = useContext(DataContext);
  const [isLoading, setIsLoading] = useState(true);
  const [startTime, setStartTime] = useState(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [remainingDataLoaded, setRemainingDataLoaded] = useState(false);
  const [minLoadDuration] = useState(500);
  const [maxLoadDuration] = useState(30000);
  const [isDataFetching, setIsDataFetching] = useState(false);
  const [loadStartTime, setLoadStartTime] = useState(null);
  
  // Use our new unified prompts hook instead of separate hooks
  const { activePrompt, clearPrompt } = useUserPrompts();

  const showLoading = isLoading || (isDataFetching && Date.now() - loadStartTime > minLoadDuration);

  // Add the tracking for initial render vs subsequent renders
  useEffect(() => {
    // Keep track of initial render vs subsequent renders
    const isInitialRender = sessionStorage.getItem('initialRender') !== 'done';
    if (isInitialRender) {
      sessionStorage.setItem('initialRender', 'done');
    }
  }, []);

  // Step 1: Initial essential data load
  useEffect(() => {
    if (initialLoadComplete) return;

    console.log("loading initial data")
    async function loadInitialData() {
      setStartTime(Date.now());
      try {
        setIsDataFetching(true);
        setLoadStartTime(Date.now());

        const baseData = await getBaseData();
        console.log("Base data loaded:", baseData);

        let userInfo = null;
        let orgInfo = {
          selectedOrganization: "",
          selectedOrganizationInfo: null,
          managedOrganizations: []
        };

        // If we have the default org from the session, set it now
        if (baseData.defaultOrg && baseData.defaultOrg.data) {
          orgInfo.selectedOrganization = baseData.defaultOrg.data;
        }

        if (baseData.loggedInEmail && baseData.loggedInEmail.data) {
          // Get user info and organization info
          userInfo = await getCurrentUser(baseData.loggedInEmail.data);
          console.log("User info loaded:", userInfo);

          if (userInfo && userInfo._id) {
            // Get organization info using both user info and the session's default org
            const userOrgInfo = await getOrganizationInfo(
              userInfo._id,
              baseData.loggedInEmail.data,
              baseData.defaultOrg?.data
            );
            console.log("Organization info loaded:", userOrgInfo);

            // Update orgInfo with user's organizations
            orgInfo = {
              ...orgInfo,
              ...userOrgInfo
            };
          }
        }

        let initialEvents = [];
        if (location.pathname === '/' || location.pathname === '/events') {
          initialEvents = await getHomePageEvents(baseData.userLoc);
        }
        else if (location.pathname.startsWith('/event/')) {
          const eventId = location.pathname.split('/')[2];
          const singleEvent = await getSingleEvent(eventId);
          initialEvents = singleEvent ? [singleEvent] : [];
        }

        // Try to get data from localStorage if API failed
        if (!userInfo) {
          const storedUser = localStorage.getItem('currentUser');
          if (storedUser) {
            try {
              userInfo = JSON.parse(storedUser);
              console.log("Loaded user from localStorage:", userInfo);
            } catch (e) {
              console.error("Failed to parse user from localStorage:", e);
            }
          }
        }

        if (orgInfo.selectedOrganization === "" && orgInfo.managedOrganizations.length === 0) {
          const storedOrg = localStorage.getItem('defaultOrganization');
          if (storedOrg) {
            try {
              const defaultOrg = JSON.parse(storedOrg);
              console.log("Loaded organization from localStorage:", defaultOrg);

              if (defaultOrg._id) {
                orgInfo.selectedOrganization = defaultOrg._id;
                orgInfo.selectedOrganizationInfo = defaultOrg;
              }
            } catch (e) {
              console.error("Failed to parse organization from localStorage:", e);
            }
          }
        }

        console.log("Setting initial app data with:", { userInfo, orgInfo });
        setAppData({
          ...baseData,
          userInfo,
          ...orgInfo,
          information: initialEvents.filter(item =>
            !item.past &&
            item.isApproved &&
            !item.isPrivate
          ),
          informationWithPastEvents: initialEvents,
          unapprovedEvents: initialEvents.filter(item => !item.isApproved)
        });

      } catch (error) {
        console.error("Error loading initial data:", error);
      } finally {
        setIsDataFetching(false);
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minLoadDuration - elapsedTime);

        setTimeout(() => {
          setIsLoading(false);
          setInitialLoadComplete(true);
        }, remainingTime);
      }
    }

    loadInitialData();
  }, []);

  // Step 2: Load remaining data after initial render
  useEffect(() => {
    if (!initialLoadComplete || remainingDataLoaded) return;

    async function loadRemainingData() {
      try {
        console.log('Starting remaining data load');
        setIsDataFetching(true);
        setLoadStartTime(Date.now());

        const [allEvents, allUsers, allOrgs] = await Promise.all([
          getAllEvents(),
          getAllUsers(),
          getAllOrganizations()
        ]);

        const eventsList = Array.isArray(allEvents) ? allEvents : [];

        setAppData(prev => {
          if (prev.userLoc?.lat && prev.userLoc?.lon) {
            eventsList.forEach(event => {
              if (event.latitude && event.longitude) {
                event.distanceFromUser = calculateDistance(
                  prev.userLoc.lat,
                  prev.userLoc.lon,
                  event.latitude,
                  event.longitude
                );
              } else {
                event.distanceFromUser = null;
              }
            });
          }

          // For user with organizations, filter the ones they have access to
          let managedOrganizations = prev.managedOrganizations;

          return {
            ...prev,
            information: eventsList.filter(item =>
              !item.past &&
              item.isApproved &&
              !item.isPrivate
            ),
            informationWithPastEvents: eventsList,
            unapprovedEvents: eventsList.filter(item => !item.isApproved),
            users: allUsers?.users ?? [],
            managedOrganizations,
            usersLoaded: true
          };
        });

        setRemainingDataLoaded(true);
      } catch (error) {
        console.error('Error loading remaining data:', error);
      } finally {
        setIsDataFetching(false);
      }
    }

    loadRemainingData();
  }, [initialLoadComplete, remainingDataLoaded]);

  // Maximum loading time handler
  useEffect(() => {
    if (isLoading && startTime) {
      const maxLoadTimeout = setTimeout(() => {
        setIsLoading(false);
        setInitialLoadComplete(true);
      }, maxLoadDuration);

      return () => clearTimeout(maxLoadTimeout);
    }
  }, [isLoading, startTime, maxLoadDuration]);

  // Instagram redirect
  // useEffect(() => {
  //   redirectToExternalBrowser();
  // }, []);

  return (
    <>
      {showLoading ? (
        <Loading />
      ) : (
        <>
          <ScrollToTop />
          <RouteErrorBoundary routeKey={location.pathname + location.search}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/events" element={<Events />} />
              <Route path="/event/:id" element={<EventRoute />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/error" element={<ErrorPage />} />
              <Route
                path="/adminPanel"
                element={
                  <RequireAdmin>
                    <AdminPanel />
                  </RequireAdmin>
                }
              />
              <Route path="/organization" element={<OrganizerHome />} />
              <Route path="/organization/:slug" element={<OrganizationProfile />} />
              <Route path="/organizations-list" element={<MyOrganizations />} />
              <Route path="/organizerHome" element={<Navigate to="/organization" replace />} />
              <Route path="/organizerSettings" element={<OrganizerSettings />} />
              <Route path="/newCreateEvent" element={<NewCreateEvent />} />
              <Route path="/login" element={<Loading />} />
              <Route path="/resetPassword" element={<ResetPassword />} />
              <Route path="/redirect" element={<Redirect />} />
              <Route path="/lists" element={<Lists />} />
              <Route path="/myOrganizations" element={<MyOrganizations />} />
              <Route path="/create-organization" element={<CreateOrganization />} />
              <Route path="/quiz" element={<OnboardingQuiz />} />
            </Routes>
          </RouteErrorBoundary>

          {/* Dynamic prompt modal - handles both quiz and phone prompts */}
          <DynamicPromptModal 
            type={activePrompt} 
            visible={activePrompt !== null} 
            onClose={clearPrompt} 
          />
        </>
      )}
    </>
  );
}

function App() {
  return (
    <AntApp>
      <BrowserRouter>
        <ScrollToTop />
        <DataProvider>
          <AnalyticsProvider>
            <ConfigProvider theme={antDesignTheme}>
              <AppContent />
            </ConfigProvider>
          </AnalyticsProvider>
        </DataProvider>
      </BrowserRouter>
    </AntApp>
  );
}

export default App;